import React, { useEffect, useState } from 'react'
import {
    Alert,
    Box,
    Button,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
    Tooltip
} from '@mui/material'
import LoadingButton from '@mui/lab/LoadingButton'
import AddIcon from '@mui/icons-material/Add'
import SaveIcon from '@mui/icons-material/Save'
import DeleteIcon from '@mui/icons-material/Delete'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'

import api from '../../../../api'
import { getSession } from '../../../../auth'
import useNotification from '../../../Notification'
import Loading from '../../../Loading'

const SECRET_PLACEHOLDER = '********'

const CRM_OPTIONS = [
    { value: 'PIPEDRIVE', label: 'Pipedrive' },
    { value: 'HUBSPOT', label: 'HubSpot' },
    { value: 'SALESFORCE', label: 'Salesforce' },
    { value: 'RDSTATION', label: 'RD Station CRM' }
]

const FIELD_CONFIG = {
    PIPEDRIVE: {
        credentials: [
            { name: 'api_token', label: 'API token' }
        ],
        settings: []
    },
    HUBSPOT: {
        credentials: [
            { name: 'access_token', label: 'Private app token' }
        ],
        settings: [
            { name: 'owner_id', label: 'Owner ID', required: false }
        ]
    },
    SALESFORCE: {
        credentials: [
            { name: 'client_id', label: 'Client ID' },
            { name: 'client_secret', label: 'Client secret', type: 'password' }
        ],
        settings: [
            { name: 'login_url', label: 'URL de login', defaultValue: 'https://login.salesforce.com' }
        ]
    },
    RDSTATION: {
        credentials: [
            { name: 'api_token', label: 'API token' }
        ],
        settings: [
            { name: 'deal_stage_id', label: 'Coluna/etapa', type: 'select', optionsKey: 'deal_stages', emptyLabel: 'Primeira coluna padrão' },
            { name: 'user_id', label: 'User ID', required: false }
        ]
    }
}

const formatError = (response) => {
    if (!response) return 'Ocorreu um erro no sistema.'
    if (typeof response.data === 'string') return response.data
    return response.data?.detail || response.data?.error || JSON.stringify(response.data) || 'Ocorreu um erro no sistema.'
}

const createLocalId = () => `new-${Date.now()}-${Math.random()}`

const emptyIntegration = () => ({
    local_id: createLocalId(),
    id: null,
    enabled: false,
    crm_type: '',
    settings: {},
    credentials: {},
    credential_status: {}
})

const ScoutCrmIntegration = () => {
    const enterpriseId = getSession()?.profile?.enterprise?.id
    const [integrations, setIntegrations] = useState([emptyIntegration()])
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(null)
    const [testing, setTesting] = useState(null)
    const [removing, setRemoving] = useState(null)
    const [loadingStages, setLoadingStages] = useState(null)
    const [stageOptions, setStageOptions] = useState({})
    const [reportEmail, setReportEmail] = useState('')
    const [savingReportEmail, setSavingReportEmail] = useState(false)

    const endpoint = `/api/enterprises/${enterpriseId}/scout_crm_integration/`

    const fillCredentialPlaceholders = (crmType, credentialStatus) => {
        const credentials = {}
        const fields = FIELD_CONFIG[crmType]?.credentials || []
        fields.forEach((field) => {
            credentials[field.name] = credentialStatus?.[field.name] ? SECRET_PLACEHOLDER : ''
        })
        return credentials
    }

    const normalizeIntegration = (data) => {
        const crmType = data.crm_type || ''
        return {
            local_id: data.local_id || createLocalId(),
            id: data.id || null,
            enabled: Boolean(data.enabled),
            crm_type: crmType,
            settings: data.settings || {},
            credential_status: data.credential_status || {},
            credentials: fillCredentialPlaceholders(crmType, data.credential_status || {})
        }
    }

    const loadData = async () => {
        setLoading(true)
        const res = await api.get(endpoint)
        setLoading(false)

        if (res.status !== 200) {
            useNotification('Ops!', 'Informações não carregadas.', 'danger')
            return
        }

        const rows = Array.isArray(res.data)
            ? res.data
            : (Array.isArray(res.data?.integrations) ? res.data.integrations : (res.data?.id || res.data?.crm_type ? [res.data] : []))
        setReportEmail(Array.isArray(res.data) ? '' : (res.data?.report_email || ''))
        setIntegrations(rows.length ? rows.map(normalizeIntegration) : [emptyIntegration()])
    }

    useEffect(() => {
        loadData()
    }, [])

    const integrationKey = (integration) => integration.id || integration.local_id

    const setIntegration = (index, patch) => {
        setIntegrations((prev) => prev.map((item, itemIndex) => (
            itemIndex === index ? { ...item, ...patch } : item
        )))
    }

    const setSetting = (index, field, value) => {
        setIntegrations((prev) => prev.map((item, itemIndex) => (
            itemIndex === index ? {
                ...item,
                settings: { ...item.settings, [field]: value }
            } : item
        )))
    }

    const setCredential = (index, field, value) => {
        setIntegrations((prev) => prev.map((item, itemIndex) => (
            itemIndex === index ? {
                ...item,
                credentials: { ...item.credentials, [field]: value }
            } : item
        )))
    }

    const changeCrm = (index, crmType) => {
        const defaultSettings = {}
        ;(FIELD_CONFIG[crmType]?.settings || []).forEach((field) => {
            if (field.defaultValue) defaultSettings[field.name] = field.defaultValue
        })
        setIntegration(index, {
            crm_type: crmType,
            settings: defaultSettings,
            credentials: {},
            credential_status: {}
        })
    }

    const payload = (integration) => ({
        id: integration.id,
        enabled: integration.enabled,
        crm_type: integration.crm_type || null,
        settings: integration.settings,
        credentials: integration.credentials
    })

    const loadDealStages = async (integration) => {
        if (integration.crm_type !== 'RDSTATION') return

        const key = integrationKey(integration)
        if (stageOptions[key]) return

        setLoadingStages(key)
        const res = await api.post(`${endpoint}deal_stages/`, payload(integration), { timeout: 30000 })
        setLoadingStages(null)

        if (res.status !== 200) {
            useNotification('Ops!', formatError(res), 'danger')
            return
        }

        setStageOptions((prev) => ({
            ...prev,
            [key]: res.data?.results || []
        }))
    }

    const submit = async (integration) => {
        const key = integrationKey(integration)
        setSaving(key)
        const res = integration.id
            ? await api.patch(`${endpoint}${integration.id}/`, payload(integration), { timeout: 20000 })
            : await api.post(endpoint, payload(integration), { timeout: 20000 })
        setSaving(null)

        if (res.status !== 200 && res.status !== 201) {
            useNotification('Ops!', formatError(res), 'danger')
            return
        }

        useNotification('Sucesso!', 'Integração Scout salva com sucesso.', 'success')
        setIntegrations((prev) => prev.map((item) => (
            integrationKey(item) === key
                ? normalizeIntegration({ ...res.data, local_id: integration.local_id })
                : item
        )))
    }

    const testConnection = async (integration) => {
        const key = integrationKey(integration)
        setTesting(key)
        const res = await api.post(`${endpoint}test/`, payload(integration), { timeout: 30000 })
        setTesting(null)

        if (res.status !== 200) {
            useNotification('Ops!', formatError(res), 'danger')
            return
        }

        useNotification('Sucesso!', 'Conexão validada com sucesso.', 'success')
    }

    const addIntegration = () => {
        setIntegrations((prev) => [...prev, emptyIntegration()])
    }

    const removeIntegration = async (integration, index) => {
        if (!integration.id) {
            setIntegrations((prev) => {
                const next = prev.filter((_, itemIndex) => itemIndex !== index)
                return next.length ? next : [emptyIntegration()]
            })
            return
        }

        const key = integrationKey(integration)
        setRemoving(key)
        const res = await api.delete(`${endpoint}${integration.id}/`)
        setRemoving(null)

        if (res.status !== 204) {
            useNotification('Ops!', formatError(res), 'danger')
            return
        }

        useNotification('Sucesso!', 'Integração removida com sucesso.', 'success')
        setIntegrations((prev) => {
            const next = prev.filter((_, itemIndex) => itemIndex !== index)
            return next.length ? next : [emptyIntegration()]
        })
    }

    const saveReportEmail = async () => {
        setSavingReportEmail(true)
        const res = await api.patch(`${endpoint}report_settings/`, { report_email: reportEmail }, { timeout: 20000 })
        setSavingReportEmail(false)

        if (res.status !== 200) {
            useNotification('Ops!', formatError(res), 'danger')
            return
        }

        setReportEmail(res.data?.report_email || '')
        useNotification('Sucesso!', 'E-mail dos relatórios salvo com sucesso.', 'success')
    }

    const selectedCrms = integrations.map((integration) => integration.crm_type).filter(Boolean)
    const canAdd = integrations.length < CRM_OPTIONS.length && !integrations.some((integration) => !integration.crm_type)

    return (
        <>
            <Loading loading={loading} />
            <Box className="flexbox input-settings" sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                    <Box className="category-title push-bottom">
                        Integrações diárias
                    </Box>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={addIntegration}
                        disabled={!canAdd}
                    >
                        Adicionar nova
                    </Button>
                </Box>

                <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField
                        size="small"
                        label="E-mail dos relatórios"
                        type="email"
                        value={reportEmail}
                        onChange={(event) => setReportEmail(event.target.value)}
                        sx={{ flex: '1 1 18rem' }}
                    />
                    <LoadingButton
                        style={{ width: 'fit-content' }}
                        onClick={saveReportEmail}
                        loading={savingReportEmail}
                        loadingPosition="start"
                        startIcon={<SaveIcon />}
                        variant="contained"
                    >
                        Salvar e-mail
                    </LoadingButton>
                </Box>

                {integrations.map((integration, index) => {
                    const key = integrationKey(integration)
                    const fieldConfig = FIELD_CONFIG[integration.crm_type] || { credentials: [], settings: [] }
                    const title = CRM_OPTIONS.find((crm) => crm.value === integration.crm_type)?.label || `Integração ${index + 1}`

                    return (
                        <Box
                            key={key}
                            sx={{
                                border: '1px solid #d8d8d8',
                                borderRadius: '4px',
                                padding: '1rem',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '1rem'
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
                                <Box sx={{ fontWeight: 600, color: '#21446c' }}>{title}</Box>
                                <Tooltip title="Remover integração">
                                    <span>
                                        <IconButton
                                            size="small"
                                            onClick={() => removeIntegration(integration, index)}
                                            disabled={removing === key}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>

                            <FormControlLabel
                                label="Ativar integração"
                                sx={{ margin: 0 }}
                                control={
                                    <Switch
                                        checked={integration.enabled}
                                        onChange={() => setIntegration(index, { enabled: !integration.enabled })}
                                    />
                                }
                            />

                            <Grid container spacing={2}>
                                <Grid item lg={6} md={6} xs={12}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel id={`scout-crm-type-label-${key}`}>CRM</InputLabel>
                                        <Select
                                            labelId={`scout-crm-type-label-${key}`}
                                            label="CRM"
                                            value={integration.crm_type}
                                            onChange={(event) => changeCrm(index, event.target.value)}
                                        >
                                            {CRM_OPTIONS.map((crm) => (
                                                <MenuItem
                                                    key={crm.value}
                                                    value={crm.value}
                                                    disabled={selectedCrms.includes(crm.value) && crm.value !== integration.crm_type}
                                                >
                                                    {crm.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {fieldConfig.settings.map((field) => {
                                    const value = integration.settings?.[field.name] || ''
                                    const options = field.optionsKey === 'deal_stages' ? (stageOptions[key] || []) : []
                                    const hasSelectedOption = options.some((option) => option.id === value)

                                    return (
                                        <Grid item lg={6} md={6} xs={12} key={field.name}>
                                            {field.type === 'select' ?
                                                <FormControl fullWidth size="small">
                                                    <InputLabel id={`scout-crm-${field.name}-label-${key}`}>{field.label}</InputLabel>
                                                    <Select
                                                        labelId={`scout-crm-${field.name}-label-${key}`}
                                                        label={field.label}
                                                        value={value}
                                                        onOpen={() => field.optionsKey === 'deal_stages' && loadDealStages(integration)}
                                                        onChange={(event) => setSetting(index, field.name, event.target.value)}
                                                    >
                                                        <MenuItem value="">{field.emptyLabel || ''}</MenuItem>
                                                        {value && !hasSelectedOption &&
                                                            <MenuItem value={value}>Coluna selecionada</MenuItem>
                                                        }
                                                        {loadingStages === key &&
                                                            <MenuItem disabled>Carregando...</MenuItem>
                                                        }
                                                        {options.map((option) => (
                                                            <MenuItem key={option.id} value={option.id}>
                                                                {option.name}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                                :
                                                <TextField
                                                    size="small"
                                                    label={field.label}
                                                    fullWidth
                                                    required={Boolean(field.required)}
                                                    value={value}
                                                    onChange={(event) => setSetting(index, field.name, event.target.value)}
                                                />
                                            }
                                        </Grid>
                                    )
                                })}
                                {fieldConfig.credentials.map((field) => (
                                    <Grid item lg={6} md={6} xs={12} key={field.name}>
                                        <TextField
                                            size="small"
                                            label={field.label}
                                            fullWidth
                                            type={field.type || 'text'}
                                            value={integration.credentials?.[field.name] || ''}
                                            onChange={(event) => setCredential(index, field.name, event.target.value)}
                                        />
                                    </Grid>
                                ))}
                            </Grid>

                            {Object.values(integration.credential_status || {}).some(Boolean) &&
                                <Alert severity="info">
                                    Credenciais já configuradas serão preservadas quando o campo permanecer como {SECRET_PLACEHOLDER}.
                                </Alert>
                            }

                            <Box sx={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                                <LoadingButton
                                    style={{ width: 'fit-content' }}
                                    onClick={() => submit(integration)}
                                    loading={saving === key}
                                    loadingPosition="start"
                                    startIcon={<SaveIcon />}
                                    variant="contained"
                                    disabled={!integration.crm_type}
                                >
                                    Salvar
                                </LoadingButton>
                                <LoadingButton
                                    style={{ width: 'fit-content' }}
                                    onClick={() => testConnection(integration)}
                                    loading={testing === key}
                                    loadingPosition="start"
                                    startIcon={<CheckCircleIcon />}
                                    variant="outlined"
                                    disabled={!integration.crm_type}
                                >
                                    Testar conexão
                                </LoadingButton>
                            </Box>
                        </Box>
                    )
                })}
            </Box>
        </>
    )
}

export default ScoutCrmIntegration
